import {useCallback, useEffect, useRef, useState} from 'react';
import FirmaService from 'src/modules/firma/firmaService';
import Errors from 'src/modules/shared/error/errors';
import {
  mergeParentTaskForConfig,
} from 'src/modules/tarea/plastyvergParentFieldsConfig';
import {getRecordId, sortChildTasksByEndDateAsc} from 'src/modules/tarea/tareaParcialHelpers';
import TareaService from 'src/modules/tarea/tareaService';

export type ChildTasksDataStatus = 'idle' | 'listing' | 'hydrating' | 'ready' | 'error';

type UseTareaChildTasksDataParams = {
  parentTask: any;
  tableMode: boolean;
  partialVerificationEnabled: boolean;
};

const HYDRATE_CONCURRENCY = 5;

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  if (!items.length) return [];
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({length: Math.min(concurrency, items.length)}, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]);
    }
  });

  await Promise.all(workers);
  return results;
}

async function findChildSafe(child: any): Promise<any> {
  const childId = getRecordId(child);
  if (!childId) return child;
  try {
    return await TareaService.find(childId);
  } catch {
    return child;
  }
}

/**
 * Pipeline list → hydrate con generation counter.
 * Un solo `status` reemplaza loading + detailLoading.
 */
export function useTareaChildTasksData({
  parentTask,
  tableMode,
  partialVerificationEnabled,
}: UseTareaChildTasksDataParams) {
  const parentId = getRecordId(parentTask);

  const [status, setStatus] = useState<ChildTasksDataStatus>('idle');
  const [children, setChildren] = useState<any[]>([]);
  const [parciales, setParciales] = useState<any[]>([]);
  const [detailedParent, setDetailedParent] = useState<any | null>(null);
  const [detailedChildren, setDetailedChildren] = useState<any[]>([]);

  const generationRef = useRef(0);
  const childrenRef = useRef(children);
  childrenRef.current = children;
  const parentTaskRef = useRef(parentTask);
  parentTaskRef.current = parentTask;

  const hydrateSnapshot = useCallback(
    async (sourceChildren: any[], generation?: number) => {
      const shouldCheckGeneration = generation != null;
      const currentParent = parentTaskRef.current;
      let parent = currentParent;
      if (parentId) {
        try {
          parent = await TareaService.find(parentId);
        } catch {
          parent = currentParent;
        }
      }
      if (shouldCheckGeneration && generation !== generationRef.current) return null;

      parent = mergeParentTaskForConfig(parent, currentParent);

      const sorted = sortChildTasksByEndDateAsc(sourceChildren.length ? sourceChildren : []);
      const childDetails = await mapWithConcurrency(sorted, HYDRATE_CONCURRENCY, findChildSafe);
      if (shouldCheckGeneration && generation !== generationRef.current) return null;

      return {parent, childDetails};
    },
    [parentId],
  );

  const reload = useCallback(
    async ({hydrate = true}: {hydrate?: boolean} = {}): Promise<{parciales: any[]} | undefined> => {
      if (!parentId) {
        setChildren([]);
        setParciales([]);
        setDetailedParent(parentTaskRef.current || null);
        setDetailedChildren([]);
        setStatus('ready');
        return {parciales: []};
      }

      const generation = ++generationRef.current;
      setStatus('listing');

      try {
        const [childrenResponse, parcialesResponse] = await Promise.all([
          TareaService.list({parentTask: parentId}, 'createdAt_DESC', 1000, 0),
          partialVerificationEnabled ? FirmaService.listParcialesByParent(parentId) : Promise.resolve([]),
        ]);

        if (generation !== generationRef.current) return;

        const nextChildren = childrenResponse?.rows || [];
        const nextParciales = parcialesResponse || [];
        setChildren(nextChildren);
        setParciales(nextParciales);

        if (!tableMode || !hydrate) {
          try {
            const parent = await TareaService.find(parentId);
            if (generation !== generationRef.current) return;
            setDetailedParent(mergeParentTaskForConfig(parent, parentTaskRef.current));
          } catch {
            // keep previous detailedParent
          }
          setStatus('ready');
          return {parciales: nextParciales};
        }

        setStatus('hydrating');
        const snapshot = await hydrateSnapshot(nextChildren, generation);
        if (!snapshot || generation !== generationRef.current) return;

        setDetailedParent(snapshot.parent);
        setDetailedChildren(snapshot.childDetails);
        setStatus('ready');
        return {parciales: nextParciales};
      } catch (error) {
        if (generation !== generationRef.current) return;
        Errors.handle(error);
        setChildren([]);
        setParciales([]);
        setDetailedParent(parentTaskRef.current || null);
        setDetailedChildren([]);
        setStatus('error');
      }
    },
    [hydrateSnapshot, parentId, partialVerificationEnabled, tableMode],
  );

  /** Hydrate-only (usa children actuales). Útil tras cambios locales o al entrar a tableMode. */
  const reloadHydrate = useCallback(async () => {
    if (!parentId || !tableMode) return;
    const generation = ++generationRef.current;
    setStatus('hydrating');
    try {
      const snapshot = await hydrateSnapshot(childrenRef.current, generation);
      if (!snapshot || generation !== generationRef.current) return;
      setDetailedParent(snapshot.parent);
      setDetailedChildren(snapshot.childDetails);
      setStatus('ready');
    } catch (error) {
      if (generation !== generationRef.current) return;
      Errors.handle(error);
      setDetailedParent(parentTaskRef.current || null);
      setDetailedChildren(sortChildTasksByEndDateAsc(childrenRef.current));
      setStatus('error');
    }
  }, [hydrateSnapshot, parentId, tableMode]);

  useEffect(() => {
    void reload({hydrate: tableMode});
    return () => {
      generationRef.current += 1;
    };
  }, [parentId, partialVerificationEnabled]); // eslint-disable-line react-hooks/exhaustive-deps -- mount/parent change only

  const prevTableModeRef = useRef(tableMode);
  useEffect(() => {
    const turnedOn = tableMode && !prevTableModeRef.current;
    prevTableModeRef.current = tableMode;
    if (turnedOn) {
      void reloadHydrate();
    }
  }, [reloadHydrate, tableMode]);

  const patchChildModeloAppFields = useCallback((childModelId: string, nextAppFields: any) => {
    setDetailedChildren((current) =>
      current.map((child) => {
        if (getRecordId(child?.modeloTarea) !== childModelId) return child;
        return {
          ...child,
          modeloTarea: {
            ...child.modeloTarea,
            appFields: nextAppFields,
          },
        };
      }),
    );
  }, []);

  const listing = status === 'listing' || status === 'idle';
  const hydrating = status === 'hydrating';
  const busy = listing || hydrating;

  return {
    status,
    listing,
    hydrating,
    busy,
    children,
    parciales,
    detailedParent,
    detailedChildren,
    reload,
    reloadHydrate,
    hydrateSnapshot: () => hydrateSnapshot(childrenRef.current),
    patchChildModeloAppFields,
  };
}
